"use client";

import * as React from "react";
import { signIn, getSession } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { safeCallbackUrl } from "@/lib/auth/safe-callback-url";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";

interface LoginFormProps {
  loginBackgroundUrl: string | null;
  loginLogoUrl: string | null;
  /** Login-logo scale in percent (50–200, default 100). */
  loginLogoScale: number;
}

export function LoginForm({
  loginBackgroundUrl,
  loginLogoUrl,
  loginLogoScale,
}: LoginFormProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations("auth");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  const locale = useLocale();
  // Untrusted: comes straight from the address bar. Only ever navigate to it
  // through `safeCallbackUrl` (same origin, relative path) — a raw value
  // allowed `javascript:` (DOM-XSS) and foreign hosts (open redirect).
  const rawCallbackUrl = searchParams.get("callbackUrl");
  const fallbackPath = `/${locale}/dashboard`;
  const ssoToken = searchParams.get("ssoToken");
  const ssoError = searchParams.get("ssoError");

  // Merkt sich den bereits eingelösten SSO-Token. Der useEffect kann durch
  // den /login → /de/login Locale-Redirect (oder React-Doppelrender) erneut
  // laufen; der SSO-Token ist aber EINMALIG (Redis löscht ihn beim ersten
  // Einlösen). Ohne diesen Guard würde der zweite Lauf einen schon
  // verbrauchten Token einlösen → CredentialsSignin → leere Session.
  const consumedSsoTokenRef = React.useRef<string | null>(null);

  // Auto-login when returning from SSO callback with a token
  React.useEffect(() => {
    if (!ssoToken) return;
    if (consumedSsoTokenRef.current === ssoToken) return;
    consumedSsoTokenRef.current = ssoToken;
    setIsLoading(true);

    signIn("credentials", {
      ssoToken,
      redirect: false,
    })
      .then(async (result) => {
        if (result?.error) {
          setError(t("ssoLoginFailed"));
          setIsLoading(false);
        } else {
          // Session serverseitig bestätigen, BEVOR wir hart navigieren —
          // sonst zeigt das Dashboard kurz die leere Session, bis der Nutzer
          // manuell aktualisiert (Race zwischen Set-Cookie und Navigation).
          await getSession();
          window.location.href = safeCallbackUrl(
            rawCallbackUrl,
            window.location.origin,
            fallbackPath,
          );
        }
      })
      .catch(() => {
        setError(t("loginError"));
        setIsLoading(false);
      });
  }, [ssoToken, rawCallbackUrl, fallbackPath, router, t]);

  // Show SSO error from callback
  React.useEffect(() => {
    if (ssoError) {
      setError(t("ssoLoginFailed"));
    }
  }, [ssoError, t]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const result = await signIn("credentials", {
        login: email,
        password,
        redirect: false,
      });

      if (result?.error) {
        setError(t("invalidCredentials"));
      } else {
        window.location.href = safeCallbackUrl(
          rawCallbackUrl,
          window.location.origin,
          fallbackPath,
        );
      }
    } catch {
      setError(t("loginError"));
    } finally {
      setIsLoading(false);
    }
  }

  function handleMicrosoftLogin() {
    window.location.href = "/api/auth/sso/authorize";
  }

  const backgroundStyle: React.CSSProperties | undefined = loginBackgroundUrl
    ? {
        backgroundImage: `url(${loginBackgroundUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : undefined;

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background px-4"
      style={backgroundStyle}
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {loginLogoUrl ? (
            /* eslint-disable-next-line @next/next/no-img-element */
            <img
              src={loginLogoUrl}
              alt="LTS Akademie"
              className="mx-auto mb-4 w-auto object-contain"
              style={{ height: `${(48 * loginLogoScale) / 100}px` }}
            />
          ) : (
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-6 w-6"
              >
                <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
          )}
          <CardTitle className="text-2xl">{t("loginTitle")}</CardTitle>
          <CardDescription>{t("loginSubtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              {error}
            </Alert>
          )}

          <Button
            variant="outline"
            className="w-full"
            onClick={handleMicrosoftLogin}
            disabled={isLoading}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 21 21" fill="none">
              <rect x="1" y="1" width="9" height="9" fill="#F25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
              <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
            </svg>
            {t("loginWithMicrosoft")}
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">
                {t("orContinueWith")}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@lts-akademie.de"
                required
                autoComplete="email"
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("password")}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={isLoading}
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "..." : t("loginButton")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
